/**
 * The database facade. Everything above this file writes plain SQL and never
 * learns which driver is underneath — that is what keeps the Capacitor port to
 * a one-line change in `pickDriver`.
 *
 * Every driver provides one function of OKANE's own, `fold(text)`: lowercase
 * the way JavaScript does it. SQLite's `lower()` and `LIKE` only fold ASCII,
 * and a search for "plaćanje" has to find "PLAĆANJE". The browser drivers
 * register it on open, the cloud server on every ledger connection.
 */
import { MIGRATIONS } from './schema'
import { inspectMigrations, runMigrations } from './migrate'

let driver = null

const isNative = () =>
  typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true

/**
 * How the database is being kept, for Settings to say: `backend` is `opfs` (a
 * file on disk), `indexeddb` (the sql.js fallback) or `native`; `moved` is
 * whether this boot carried it over from IndexedDB.
 */
export const storageInfo = { backend: null, moved: false }

/**
 * The browser keeps the database in a file on disk (OPFS), moving it there from
 * IndexedDB the first time (`storage.js`). A browser without OPFS keeps the old
 * IndexedDB driver — unless the database has already moved to disk, in which
 * case the old copy is stale and opening it would quietly show old data, so
 * that is an error instead.
 *
 * Rejects with `code: 'db-locked'` when another tab has the database open.
 */
async function pickDriver({ file } = {}) {
  if (isNative()) {
    const mod = await import('./drivers/capacitor.js')
    storageInfo.backend = 'native'
    return mod.createDriver()
  }
  const storage = await import('./storage.js')
  const store = await storage.legacyStore()
  const record = await storage.readRecord(store)
  try {
    const { createDriver } = await import('./drivers/opfs.js')
    const opfs = await createDriver({ file })
    // The storage steps are the local ledger's history; an org's copy was born on disk.
    const results = file ? [] : await storage.runStorageSteps({ store, driver: opfs })
    storageInfo.backend = 'opfs'
    storageInfo.moved = results.some((r) => r.moved)
    return opfs
  } catch (err) {
    if (err?.code === 'locked') throw Object.assign(new Error(err.message), { code: 'db-locked' })
    if (err?.code === 'unsupported' && !storage.onDisk(record)) {
      const mod = await import('./drivers/sqljs.js')
      storageInfo.backend = 'indexeddb'
      return mod.createDriver({ file })
    }
    if (err?.code === 'unsupported') {
      throw Object.assign(
        new Error('OKANE keeps its data in a file this browser can no longer open. Use the browser it was set up in.'),
        { code: 'storage-lost' },
      )
    }
    throw err
  }
}

/**
 * This device's own database, opened beside whatever the facade has open —
 * for moving the ledger in or out of the cloud (`db/move.js`). Rejects with
 * `code: 'db-locked'` like the ordinary open. The caller closes or erases it.
 */
export const openLocalDriver = (options) => pickDriver(options)

/** The file an encrypted org's copy is kept in on this device. */
export const vaultFile = (orgId) => `vault-${orgId}.sqlite`

/**
 * Destroys this device's copy of the ledger once it lives in the cloud: the
 * database file, the IndexedDB copy kept from before the move onto disk, and
 * the safety copies taken before migrations — every one of them is the ledger.
 * The record of storage steps stays: it says where the database lives, not
 * what is in it.
 */
export async function eraseLocal(local) {
  await local.erase()
  if (typeof indexedDB === 'undefined') return
  const storage = await import('./storage.js')
  const store = await storage.legacyStore()
  await store.delete('okane.sqlite')
  await store.delete(storage.KEPT_KEY)
  await new Promise((resolve) => {
    const req = indexedDB.deleteDatabase('okane-backups')
    req.onsuccess = req.onerror = req.onblocked = () => resolve()
  })
}

/** The driver the facade has open (the org's socket, in cloud mode). */
export const activeDriver = () => driver

/**
 * Open the database. Migrations do NOT run here by default — boot drives them
 * separately so it can show progress and take a backup first (see main.js).
 * Tests and headless callers pass `autoMigrate: true` for the old one-shot
 * behaviour.
 *
 * @param {object} [options]
 * @param {object} [options.driver]      a ready-made driver, for tests and for
 *                                       platforms that are not browser/Capacitor.
 * @param {string} [options.file]        another file than the local ledger's: an
 *                                       encrypted org's copy (`vaultFile(org)`).
 * @param {boolean} [options.autoMigrate=false] apply pending migrations inline.
 */
export async function openDatabase(options = {}) {
  if (driver) return driver
  driver = options.driver ?? (await pickDriver({ file: options.file }))
  if (options.autoMigrate) await runMigrations({ driver, migrations: MIGRATIONS })
  return driver
}

/** Where the open database stands relative to the code's migrations. */
export const migrationState = () => inspectMigrations(driver, MIGRATIONS)

/**
 * Apply pending migrations against the open database.
 * @param {object} [o]
 * @param {(e:object)=>void} [o.onProgress]
 * @param {(bytes:Uint8Array, meta:object)=>Promise<void>} [o.backup]
 */
export const migrateNow = (o = {}) =>
  runMigrations({ driver, migrations: MIGRATIONS, onProgress: o.onProgress, backup: o.backup })

/**
 * While sync applies another device's changes (or replaces the file with a
 * snapshot), the app waits: a write landing inside that transaction would not
 * be captured, and a read could see half of it. `hold(fn)` runs `fn` with the
 * gate shut; `direct` is the database without the gate, for the one holding it.
 */
let gate = null
export async function hold(fn) {
  while (gate) await gate
  let open
  gate = new Promise((resolve) => (open = resolve))
  try {
    return await fn()
  } finally {
    gate = null
    open()
  }
}
export const direct = {
  all: (sql, params) => driver.all(sql, params),
  run: (sql, params) => driver.run(sql, params),
  exec: (sql) => driver.exec(sql),
}

/** Rows for a SELECT. */
export const query = (sql, params) => (gate ? gate.then(() => query(sql, params)) : driver.all(sql, params))

/** Told after every write through `run` (not `direct`'s): sync's cue to push. */
export const afterWrite = new Set()
const wrote = (result) => {
  for (const listener of afterWrite) listener()
  return result
}

/** A single statement with no result. */
export const run = (sql, params) =>
  gate ? gate.then(() => run(sql, params)) : afterWrite.size ? Promise.resolve(driver.run(sql, params)).then(wrote) : driver.run(sql, params)

/** First row of a SELECT, or undefined. */
export async function queryOne(sql, params) {
  const rows = await query(sql, params)
  return rows[0]
}

/** Builds `INSERT INTO t (…) VALUES (…)` from an object, skipping undefined. */
export function insert(table, row) {
  const cols = Object.keys(row).filter((k) => row[k] !== undefined)
  const placeholders = cols.map(() => '?').join(', ')
  return run(
    `INSERT INTO ${table} (${cols.join(', ')}) VALUES (${placeholders})`,
    cols.map((c) => row[c]),
  )
}

/** Builds `UPDATE t SET … WHERE id = ?` from an object, skipping undefined. */
export function update(table, id, patch) {
  const cols = Object.keys(patch).filter((k) => patch[k] !== undefined && k !== 'id')
  if (!cols.length) return Promise.resolve()
  return run(
    `UPDATE ${table} SET ${cols.map((c) => `${c} = ?`).join(', ')} WHERE id = ?`,
    [...cols.map((c) => patch[c]), id],
  )
}

export const remove = (table, id) => run(`DELETE FROM ${table} WHERE id = ?`, [id])

export const exportDatabase = () => driver.export()
export const importDatabase = (bytes) => driver.import(bytes)
export const flushDatabase = () => driver.flush()
